'use client';

import React, { useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { DocumentIcon, TrashIcon, ShareIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { tr, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

interface Notification {
  id: string;
  type: 'fileUpload' | 'fileDelete' | 'fileShare' | 'storageWarning' | 'error';
  message: string;
  timestamp: Date;
}

interface NotificationCenterProps {
  notifications: Notification[];
  onClearAll: () => void;
}

const getNotificationIcon = (type: Notification['type']) => {
  switch (type) {
    case 'fileUpload':
      return <DocumentIcon className="h-5 w-5 text-success" />;
    case 'fileDelete':
      return <TrashIcon className="h-5 w-5 text-destructive" />;
    case 'fileShare':
      return <ShareIcon className="h-5 w-5 text-accent" />;
    case 'storageWarning':
      return <ExclamationTriangleIcon className="h-5 w-5 text-warning" />;
    case 'error':
      return <ExclamationTriangleIcon className="h-5 w-5 text-destructive" />;
  }
};

export const NotificationCenter: React.FC<NotificationCenterProps> = ({ notifications, onClearAll }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language.startsWith('tr') ? tr : enUS;

  // The suite topbar's bell opens the panel: the adapter answers chrome:bell
  // by broadcasting this window event.
  useEffect(() => {
    const onOpen = () => setIsOpen(true);
    window.addEventListener('sak:open-notifications', onOpen);
    return () => window.removeEventListener('sak:open-notifications', onOpen);
  }, []);

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 overflow-hidden z-50"
        onClose={() => setIsOpen(false)}
      >
        <div className="absolute inset-0 overflow-hidden">
          <Transition.Child
            as={React.Fragment}
            enter="ease-in-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="absolute inset-0 bg-overlay transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
            <Transition.Child
              as={React.Fragment}
              enter="transform transition ease-in-out duration-300"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="relative w-96">
                <div className="h-full flex flex-col bg-card shadow-xl">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-border-color">
                    <Dialog.Title className="text-lg font-medium">{t('notifications.title')}</Dialog.Title>
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={onClearAll}
                        className="text-sm text-muted-foreground hover:text-foreground"
                      >
                        {t('notifications.clearAll')}
                      </button>
                      <button
                        onClick={() => setIsOpen(false)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="flex items-center justify-center h-full text-muted-foreground">
                        {t('notifications.empty')}
                      </div>
                    ) : (
                      <div className="divide-y divide-border-color">
                        {notifications.map((notification) => (
                          <div
                            key={notification.id}
                            className="p-4 hover:bg-muted transition-colors"
                          >
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0">
                                {getNotificationIcon(notification.type)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-foreground">{notification.message}</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {format(notification.timestamp, 'dd MMMM yyyy HH:mm', { locale: dateLocale })}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};